import { StrictMode, Suspense, lazy, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import './app.css'
import './i18n'
import { api } from './lib/api'
import { useAuthStore } from './lib/store/auth'
import type { UserProfile } from '@mindweb/shared'
import { LandingPage } from './pages/landing'
import { AuthGuard } from './components/ui/auth-guard'
import { GlobalLoginDialog } from './components/ui/global-login-dialog'
import { useTheme } from './lib/use-theme'

// Route-level code splitting: the landing page (`/`) is the SEO-critical first
// entry and stays statically imported so it renders without a Suspense flash.
// Every other route — especially the heavy graph canvas (@xyflow/react) and
// markdown/highlight-laden share page — is lazy-loaded so visitors landing on
// `/` never download that code. Named exports are mapped to `default` for lazy.
const LoginPage = lazy(() =>
  import('./pages/login').then((m) => ({ default: m.LoginPage })),
)
const GithubCallbackPage = lazy(() =>
  import('./pages/auth-callback').then((m) => ({ default: m.GithubCallbackPage })),
)
const DashboardPage = lazy(() =>
  import('./pages/dashboard').then((m) => ({ default: m.DashboardPage })),
)
const GraphPage = lazy(() =>
  import('./pages/graph').then((m) => ({ default: m.GraphPage })),
)
const PricingPage = lazy(() =>
  import('./pages/pricing').then((m) => ({ default: m.PricingPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/settings').then((m) => ({ default: m.SettingsPage })),
)
const AdminPage = lazy(() =>
  import('./pages/admin').then((m) => ({ default: m.AdminPage })),
)
const SharePage = lazy(() =>
  import('./pages/share').then((m) => ({ default: m.SharePage })),
)
const ExplorePage = lazy(() =>
  import('./pages/explore').then((m) => ({ default: m.ExplorePage })),
)

// Public SEO content pages (pillar, comparisons, persona use-cases). Lazy so they
// stay out of the landing bundle; statically prerendered at build for crawlers.
const WhatIsKnowledgeGraphPage = lazy(() =>
  import('./pages/marketing/what-is-a-knowledge-graph').then((m) => ({
    default: m.WhatIsKnowledgeGraphPage,
  })),
)
const AiKnowledgeGraphGeneratorPage = lazy(() =>
  import('./pages/marketing/ai-knowledge-graph-generator').then((m) => ({
    default: m.AiKnowledgeGraphGeneratorPage,
  })),
)
const DeepResearchAiToolPage = lazy(() =>
  import('./pages/marketing/deep-research-ai-tool').then((m) => ({
    default: m.DeepResearchAiToolPage,
  })),
)
const VisualResearchToolPage = lazy(() =>
  import('./pages/marketing/visual-research-tool').then((m) => ({
    default: m.VisualResearchToolPage,
  })),
)
const ComparePerplexityPage = lazy(() =>
  import('./pages/marketing/compare-perplexity').then((m) => ({ default: m.ComparePerplexityPage })),
)
const CompareNotebooklmPage = lazy(() =>
  import('./pages/marketing/compare-notebooklm').then((m) => ({ default: m.CompareNotebooklmPage })),
)
const CompareConnectedPapersPage = lazy(() =>
  import('./pages/marketing/compare-connected-papers').then((m) => ({
    default: m.CompareConnectedPapersPage,
  })),
)
const CompareGensparkPage = lazy(() =>
  import('./pages/marketing/compare-genspark').then((m) => ({ default: m.CompareGensparkPage })),
)
const CompareObsidianPage = lazy(() =>
  import('./pages/marketing/compare-obsidian').then((m) => ({ default: m.CompareObsidianPage })),
)
const UseCasesResearchersPage = lazy(() =>
  import('./pages/marketing/use-cases-researchers').then((m) => ({
    default: m.UseCasesResearchersPage,
  })),
)
const UseCasesStudentsPage = lazy(() =>
  import('./pages/marketing/use-cases-students').then((m) => ({ default: m.UseCasesStudentsPage })),
)
const UseCasesFoundersPage = lazy(() =>
  import('./pages/marketing/use-cases-founders').then((m) => ({ default: m.UseCasesFoundersPage })),
)
const UseCasesKnowledgeWorkersPage = lazy(() =>
  import('./pages/marketing/use-cases-knowledge-workers').then((m) => ({
    default: m.UseCasesKnowledgeWorkersPage,
  })),
)

// Blog: SEO content section. List + per-slug article, both lazy and prerendered.
const BlogIndexPage = lazy(() =>
  import('./pages/blog/index').then((m) => ({ default: m.BlogIndexPage })),
)
const BlogPostPage = lazy(() =>
  import('./pages/blog/post').then((m) => ({ default: m.BlogPostPage })),
)

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}

function ProfileSync() {
  const { isAuthenticated, setUser } = useAuthStore()

  useEffect(() => {
    if (!isAuthenticated) return
    api.get<UserProfile>('/api/me').then((r) => {
      if (r.success && r.data) setUser(r.data)
    })
  }, [isAuthenticated])

  return null
}

function ThemeApplier() {
  useTheme()
  return null
}


createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <ThemeApplier />
        <ProfileSync />
        <GlobalLoginDialog />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/github/callback" element={<GithubCallbackPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <DashboardPage />
              </AuthGuard>
            }
          />
          <Route
            path="/graph/:id"
            element={
              <AuthGuard>
                <GraphPage />
              </AuthGuard>
            }
          />
          <Route path="/share/:id" element={<SharePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/what-is-a-knowledge-graph" element={<WhatIsKnowledgeGraphPage />} />
          <Route path="/ai-knowledge-graph-generator" element={<AiKnowledgeGraphGeneratorPage />} />
          <Route path="/deep-research-ai-tool" element={<DeepResearchAiToolPage />} />
          <Route path="/visual-research-tool" element={<VisualResearchToolPage />} />
          <Route path="/compare/perplexity" element={<ComparePerplexityPage />} />
          <Route path="/compare/notebooklm" element={<CompareNotebooklmPage />} />
          <Route path="/compare/connected-papers" element={<CompareConnectedPapersPage />} />
          <Route path="/compare/genspark" element={<CompareGensparkPage />} />
          <Route path="/compare/obsidian" element={<CompareObsidianPage />} />
          <Route path="/use-cases/researchers" element={<UseCasesResearchersPage />} />
          <Route path="/use-cases/students" element={<UseCasesStudentsPage />} />
          <Route path="/use-cases/founders" element={<UseCasesFoundersPage />} />
          <Route path="/use-cases/knowledge-workers" element={<UseCasesKnowledgeWorkersPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <SettingsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/admin"
            element={
              <AuthGuard>
                <AdminPage />
              </AuthGuard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>,
)
